import React, { useCallback, useMemo, useState } from 'react';
import { FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup, TextField } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from '../../../MeetingStaffContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import MotionVotingControls from '../../../MotionVotingControls';
import { ActivityType, SignificantType } from 'store/enums/BallotReviewTypes';
import { MeetingSession, Motion } from 'store/types/Meeting';
import { defaultTinyMCEProps } from 'util/form';
import {
  createImagesUploadHandler,
  excludeAttendee,
  getAttendeeOptions,
  meetingSessionToActivity,
  significantTypeLabel,
  useSafeAttendeeDisplayValue,
} from 'util/meeting';

const SignificantMotion: React.FunctionComponent = () => {
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [type, setType] = useState<SignificantType | undefined>();
  const [by, setBy] = useState('');
  const [second, setSecond] = useState('');
  const [discussion, setDiscussion] = useState('');

  const byAttendees = useMemo(() => excludeAttendee(attendees || [], second), [attendees, second]);
  const secondAttendees = useMemo(() => excludeAttendee(attendees || [], by), [attendees, by]);

  const byAttendeeOptions = useMemo(
    () => getAttendeeOptions(byAttendees || [], meetingSession?.presidingChairId),
    [byAttendees, meetingSession?.presidingChairId]
  );
  const secondAttendeeOptions = useMemo(
    () => getAttendeeOptions(secondAttendees || [], meetingSession?.presidingChairId),
    [secondAttendees, meetingSession?.presidingChairId]
  );

  const safeBy = useSafeAttendeeDisplayValue(by, byAttendees);
  const safeSecond = useSafeAttendeeDisplayValue(second, secondAttendees);

  const currentMotion: Motion = useMemo(() => {
    return {
      id: `significant-motion`,
      title: 'Not Significant Finding',
      statement: type
        ? type === SignificantType.VoteToDetermine
          ? 'The Negative is "not significant."'
          : significantTypeLabel[type]
        : '',
      by: safeBy,
      second: safeSecond,
      discussion,
    };
  }, [type, safeBy, safeSecond, discussion]);

  const handleClearMotion = useCallback(() => {
    setType(undefined);
    setBy('');
    setSecond('');
    setDiscussion('');
  }, []);

  const handleDisplayMotion = useCallback(
    (updatedSession: MeetingSession) => {
      logActivity?.({
        ...meetingSessionToActivity(updatedSession),
        type: ActivityType.Motion,
        motion: updatedSession.currentMotion,
      });
    },
    [logActivity]
  );

  return (
    <GridContainer>
      <GridItem>
        <FormControl>
          <FormLabel>Motion and Reason</FormLabel>
          <RadioGroup
            name="related-type-radio-group"
            value={type ?? ''}
            onChange={(event) => setType(event.target.value as SignificantType)}
          >
            <FormControlLabel
              value={SignificantType.NotSignificant}
              control={<Radio />}
              label={significantTypeLabel[SignificantType.NotSignificant]}
            />
            <FormControlLabel
              value={SignificantType.Significant}
              control={<Radio />}
              label={significantTypeLabel[SignificantType.Significant]}
            />
            <FormControlLabel
              value={SignificantType.VoteToDetermine}
              control={<Radio />}
              label={significantTypeLabel[SignificantType.VoteToDetermine]}
            />
          </RadioGroup>
        </FormControl>
      </GridItem>
      {type === SignificantType.VoteToDetermine && (
        <>
          <GridItem md={6}>
            <TextField
              label={'By'}
              select
              required
              value={safeBy}
              onChange={(event) => {
                setBy(event.target.value);
              }}
            >
              <MenuItem value={''}>-- None --</MenuItem>
              {byAttendeeOptions}
            </TextField>
          </GridItem>
          <GridItem md={6}>
            <TextField
              label={'Second'}
              select
              required
              value={safeSecond}
              onChange={(event) => {
                setSecond(event.target.value);
              }}
            >
              <MenuItem value={''}>-- None --</MenuItem>
              {secondAttendeeOptions}
            </TextField>
          </GridItem>
          <GridItem>
            <FormControl className="w-100">
              <FormLabel>Discussion</FormLabel>
              <Editor
                {...defaultTinyMCEProps}
                id="significant-discussion-editor"
                value={discussion}
                onEditorChange={(newValue: string) => setDiscussion(newValue)}
                init={{
                  ...defaultTinyMCEProps.init,
                  images_upload_handler: createImagesUploadHandler(meetingId),
                }}
              />
            </FormControl>
          </GridItem>
        </>
      )}
      <GridItem>
        <MotionVotingControls
          currentMotion={currentMotion}
          requiredFields={type === SignificantType.VoteToDetermine ? ['statement', 'by', 'second'] : ['statement']}
          onDisplayMotion={type === SignificantType.VoteToDetermine ? undefined : handleDisplayMotion}
          onClearMotion={handleClearMotion}
          disableVoting={type !== SignificantType.VoteToDetermine}
          showVotingRuleSelector={false}
        />
      </GridItem>
    </GridContainer>
  );
};

export default SignificantMotion;
